import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ff } from '../../../theme';
import type { SubmissionCardStatus, SubmissionTone } from '../utils/submissionCards';

const PILL: Record<SubmissionTone, { bg: string; color: string }> = {
  dark: { bg: ff.ink, color: ff.lime },
  red: { bg: '#FBE1DC', color: '#A1260F' },
  grey: { bg: '#F0F0EE', color: ff.body },
  green: { bg: '#E4F3CC', color: '#2C5208' },
};

interface StatusCardProps {
  /** Platform logo (FaInstagram / FaTiktok) or UGC piece icon (FiVideo / FiCamera). */
  icon: React.ElementType;
  title: string;
  /** What to do next / what this is, e.g. "Submit Instagram Reel link". */
  sub: string;
  status: SubmissionCardStatus;
  /** Brand / staff note shown inline when edits were requested. */
  feedback?: string | null;
  feedbackFrom?: string;
  onClick: () => void;
  /** Still shown, but nothing to do here — dimmed and unclickable (e.g. a deliverable the brand removed). */
  isInactive?: boolean;
  /** UGC pieces use the smaller 36px / radius-10 tile. */
  smallIcon?: boolean;
}

/** Submission card (boards SubmitPosts / SubmitUGC): icon tile, title + sub, status pill, inline feedback. */
const StatusCard: React.FC<StatusCardProps> = ({ icon: IconEl, title, sub, status, feedback, feedbackFrom, onClick, isInactive = false, smallIcon = false }) => {
  const pill = PILL[status.tone];
  return (
    <Box
      as="button"
      type="button"
      onClick={isInactive ? undefined : onClick}
      disabled={isInactive}
      aria-label={`${title}, ${sub}, ${status.pill}`}
      w="100%"
      p="14px"
      borderRadius="18px"
      textAlign="left"
      display="flex"
      flexDirection="column"
      gap="10px"
      color={ff.ink}
      fontFamily="display"
      border={isInactive ? `1px dashed ${ff.border}` : `1px solid ${status.tone === 'red' ? '#F0B8AC' : ff.card}`}
      bg={isInactive ? '#F7F7F5' : 'white'}
      opacity={isInactive ? 0.55 : 1}
      cursor={isInactive ? 'default' : 'pointer'}
      _disabled={{ cursor: 'default' }}
    >
      <Flex align="center" gap="12px" w="100%">
        <Flex flex="none" w={smallIcon ? '36px' : '44px'} h={smallIcon ? '36px' : '44px'} borderRadius={smallIcon ? '10px' : '12px'} bg={ff.grey} align="center" justify="center" color={ff.ink}>
          <IconEl size={20} aria-hidden />
        </Flex>
        <Flex flex={1} minW={0} direction="column" gap="2px">
          <Text fontSize="16px" fontWeight={600} lineHeight="normal">{title}</Text>
          <Text
            fontSize="13px"
            color="#55555A"
            lineHeight="normal"
            textDecoration={isInactive ? 'line-through' : undefined}
          >
            {sub}
          </Text>
        </Flex>
        <Flex
          as="span"
          flex="none"
          h="28px"
          px="10px"
          borderRadius="full"
          align="center"
          fontSize="12px"
          fontWeight={600}
          whiteSpace="nowrap"
          lineHeight="normal"
          bg={pill.bg}
          color={pill.color}
        >
          {status.pill}
        </Flex>
      </Flex>
      {feedback && !isInactive && (
        <Flex
          as="span"
          direction="column"
          gap="2px"
          w="100%"
          px="12px"
          py="10px"
          borderRadius="12px"
          bg="#FBE1DC"
          color="#6E1A0A"
          fontSize="14px"
          lineHeight={1.4}
        >
          {feedbackFrom && (
            <Text as="strong" fontSize="12px" letterSpacing="0.06em" textTransform="uppercase">{feedbackFrom}</Text>
          )}
          <Text as="span" noOfLines={3}>“{feedback}”</Text>
        </Flex>
      )}
    </Box>
  );
};

export default React.memo(StatusCard);
